<template>
  <div
    ref="containerRef"
    :class="[
      'multiselect-v2 tw:relative tw:w-full tw:flex tw:items-center tw:gap-2 tw:rounded-[8px] tw:border tw:transition-all tw:duration-150',
      sizeClass,
      stateClasses,
      { 'skeleton-play': skeleton },
    ]"
    @mouseenter="hovered = true"
    @mouseleave="hovered = false"
    @click="toggle"
  >
    <!-- Tags + input area -->
    <div class="tw:flex tw:flex-nowrap tw:items-center tw:gap-1 tw:flex-1 tw:min-w-0 tw:overflow-hidden">
      <!-- Selected tags -->
      <span
        v-for="item in visibleTags"
        :key="getOptionKey(item, 0)"
        class="tw:inline-flex tw:items-center tw:gap-1 tw:bg-(--surface-color) tw:border tw:border-(--divider-color) tw:rounded-full tw:px-2 tw:py-0.5 tw:text-xs tw:text-(--fontcolor) tw:max-w-[150px] tw:shrink-0"
      >
        <span class="tw:truncate">{{ getOptionLabel(item) }}</span>
        <button
          v-if="!disabled"
          type="button"
          class="tw:border-none tw:bg-transparent tw:cursor-pointer tw:text-(--text-color) tw:text-xs tw:leading-none tw:p-0 hover:tw:text-(--fontcolor)"
          @click.stop="removeItem(item)"
        >×</button>
      </span>

      <!-- Overflow count -->
      <span
        v-if="overflowCount > 0"
        class="tw:text-xs tw:text-(--text-color) tw:font-semibold tw:shrink-0"
      >+{{ overflowCount }}</span>

      <!-- Placeholder -->
      <span
        v-if="selectedItems.length === 0"
        class="tw:text-(--placeholder-color) tw:truncate"
      >{{ resolvedPlaceholder }}</span>
    </div>

    <!-- Chevron -->
    <IconGoogle
      :name="isOpen ? 'expand_less' : 'expand_more'"
      size="1.25rem"
      color="var(--text-color)"
      class="tw:shrink-0"
    />

    <!-- Dropdown -->
    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="dropdownRef"
        class="tw:fixed tw:bg-(--bg-input) tw:border tw:border-(--input-border-color) tw:rounded-lg tw:shadow-lg tw:overflow-hidden tw:z-[200000] tw:flex tw:flex-col"
        :style="dropdownStyle"
      >
        <!-- Search -->
        <div class="tw:p-2 tw:border-b tw:border-(--divider-color)">
          <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="tw:w-full tw:bg-transparent tw:border-none tw:outline-none tw:text-sm tw:text-(--fontcolor) tw:placeholder-(--placeholder-color)"
            :placeholder="resolvedSearchPlaceholder"
            @click.stop
          />
        </div>

        <!-- Select all -->
        <div
          v-if="enableSelectAll && filteredOptions.length > 1"
          class="tw:flex tw:items-center tw:justify-between tw:px-3 tw:py-2 tw:border-b tw:border-(--divider-color)"
        >
          <button
            type="button"
            class="tw:border-none tw:bg-transparent tw:text-xs tw:text-(--maincolor) tw:underline tw:cursor-pointer"
            @click.stop="toggleSelectAll"
          >
            {{ isAllSelected ? resolvedDeselectAllText : resolvedSelectAllText }}
          </button>
          <span class="tw:text-xs tw:text-(--text-color)">{{ selectedItems.length }}/{{ options.length }}</span>
        </div>

        <!-- Options list -->
        <div class="tw:overflow-y-auto tw:max-h-[240px]">
          <div
            v-if="filteredOptions.length === 0"
            class="tw:px-3 tw:py-2 tw:text-sm tw:text-(--text-color)"
          >
            {{ resolvedNoOptionsText }}
          </div>
          <div
            v-for="(option, index) in filteredOptions"
            :key="getOptionKey(option, index)"
            :class="[
              'tw:flex tw:items-center tw:gap-2 tw:px-3 tw:py-2 tw:text-sm tw:cursor-pointer tw:transition-colors tw:duration-100',
              isSelected(option)
                ? 'tw:bg-black/10 dark:tw:bg-white/10'
                : 'hover:tw:bg-black/5 dark:hover:tw:bg-white/10',
            ]"
            @click.stop="toggleOption(option)"
          >
            <CheckboxPlay
              :id="`ms-opt-${getOptionKey(option, index)}`"
              name="multiselect-option"
              :model-value="isSelected(option)"
              :checked-value="true"
              class="tw:pointer-events-none"
            >
              <template #default />
            </CheckboxPlay>
            <span class="tw:text-(--fontcolor) tw:truncate">{{ getOptionLabel(option) }}</span>
          </div>
        </div>

        <!-- Taggable input -->
        <div
          v-if="taggable"
          class="tw:p-2 tw:border-t tw:border-(--divider-color) tw:flex tw:gap-2"
        >
          <input
            v-model="newTagValue"
            type="text"
            class="tw:flex-1 tw:bg-transparent tw:border tw:border-(--input-border-color) tw:rounded tw:px-2 tw:py-1 tw:text-xs tw:text-(--fontcolor) tw:outline-none"
            :placeholder="t('multiselect.add_new')"
            @click.stop
            @keypress.enter.stop="addTag"
          />
          <button
            type="button"
            class="tw:border-none tw:bg-(--maincolor) tw:text-white tw:rounded tw:px-2 tw:py-1 tw:text-xs tw:cursor-pointer"
            @click.stop="addTag"
          >+</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import IconGoogle from '../../atoms/IconGoogle/IconGoogle.vue'
import CheckboxPlay from '../../atoms/CheckboxPlay/CheckboxPlay.vue'

const { t } = useI18n()

export interface MultiselectOption {
  [key: string]: unknown
}

const props = withDefaults(defineProps<{
  modelValue?: MultiselectOption[] | null
  options?: MultiselectOption[]
  placeholder?: string
  name?: string
  label?: string
  trackBy?: string
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  skeleton?: boolean
  error?: boolean
  searchPlaceholder?: string
  noOptionsText?: string
  enableSelectAll?: boolean
  closeOnSelect?: boolean
  allowEmpty?: boolean
  taggable?: boolean
  max?: number | null
  limit?: number
  hideSelected?: boolean
}>(), {
  modelValue: () => [],
  options: () => [],
  placeholder: undefined,
  name: undefined,
  label: 'label',
  trackBy: 'id',
  size: 'md',
  disabled: false,
  skeleton: false,
  error: false,
  searchPlaceholder: undefined,
  noOptionsText: undefined,
  enableSelectAll: false,
  closeOnSelect: false,
  allowEmpty: true,
  taggable: false,
  max: null,
  limit: 3,
  hideSelected: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: MultiselectOption[]]
  'select': [option: MultiselectOption]
  'remove': [option: MultiselectOption]
}>()

// i18n defaults
const resolvedPlaceholder = computed(() => props.placeholder ?? t('multiselect.placeholder'))
const resolvedSearchPlaceholder = computed(() => props.searchPlaceholder ?? t('multiselect.search'))
const resolvedNoOptionsText = computed(() => props.noOptionsText ?? t('multiselect.no_options'))
const resolvedSelectAllText = computed(() => t('multiselect.select_all'))
const resolvedDeselectAllText = computed(() => t('multiselect.unselect_all'))

const containerRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const searchInputRef = ref<HTMLInputElement | null>(null)
const isOpen = ref(false)
const hovered = ref(false)
const searchQuery = ref('')
const newTagValue = ref('')
const dropdownStyle = ref<Record<string, string>>({})

// --- Size classes ---
const sizeClass = computed(() => ({
  sm: 'tw:min-h-8 tw:px-2 tw:py-1 tw:text-xs',
  md: 'tw:min-h-[46px] tw:px-3 tw:py-1.5 tw:text-sm',
  lg: 'tw:min-h-[52px] tw:px-4 tw:py-2 tw:text-base',
}[props.size]))

// --- State classes ---
const stateClasses = computed(() => {
  if (props.disabled) return 'tw:border-(--input-border-color) tw:bg-(--bg-input) tw:opacity-60 tw:cursor-not-allowed'
  if (props.error) return 'tw:border-(--feedback-error-500) tw:bg-(--bg-input) tw:cursor-pointer'
  if (isOpen.value) return 'tw:border-(--fontcolor) tw:bg-(--bg-input) tw:ring-2 tw:ring-(--fontcolor)/10 tw:cursor-pointer'
  if (hovered.value) return 'tw:border-(--fontcolor) tw:bg-(--bg-input) tw:cursor-pointer'
  return 'tw:border-(--input-border-color) tw:bg-(--bg-input) tw:cursor-pointer'
})

// --- Selected items ---
const selectedItems = computed(() => (props.modelValue ?? []).filter(item => item != null))

const visibleTags = computed(() => selectedItems.value.slice(0, props.limit))

const overflowCount = computed(() => Math.max(0, selectedItems.value.length - props.limit))

const isAllSelected = computed(() =>
  props.options.length > 0 && selectedItems.value.length === props.options.length
)

// --- Option helpers ---
function getOptionKey(option: MultiselectOption, index: number): string | number {
  if (!option) return index
  return (option[props.trackBy] as string | number) ?? index
}

function getOptionLabel(option: MultiselectOption): string {
  if (!option) return ''
  return String(option[props.label] ?? '')
}

function isSelected(option: MultiselectOption): boolean {
  if (!option) return false
  return selectedItems.value.some(item => item && item[props.trackBy] === option[props.trackBy])
}

// --- Filtered options ---
const filteredOptions = computed(() => {
  let opts = props.options
  if (props.hideSelected) {
    opts = opts.filter(opt => !isSelected(opt))
  }
  if (!searchQuery.value) return opts
  const q = searchQuery.value.toLowerCase()
  return opts.filter(opt => getOptionLabel(opt).toLowerCase().includes(q))
})

// --- Actions ---
function toggle() {
  if (props.disabled || props.skeleton) return
  if (isOpen.value) {
    close()
  } else {
    open()
  }
}

function open() {
  isOpen.value = true
  searchQuery.value = ''
  nextTick(() => {
    positionDropdown()
    searchInputRef.value?.focus()
    requestAnimationFrame(() => {
      document.addEventListener('mousedown', handleOutsideClick)
    })
  })
}

function close() {
  isOpen.value = false
  document.removeEventListener('mousedown', handleOutsideClick)
}

function handleOutsideClick(e: MouseEvent) {
  const target = e.target as Node
  if (containerRef.value?.contains(target) || dropdownRef.value?.contains(target)) return
  close()
}

function toggleOption(option: MultiselectOption) {
  if (isSelected(option)) {
    removeItem(option)
  } else {
    addItem(option)
  }
  if (props.closeOnSelect) close()
}

function addItem(option: MultiselectOption) {
  if (props.max && selectedItems.value.length >= props.max) return
  const updated = [...selectedItems.value, option]
  emit('update:modelValue', updated)
  emit('select', option)
}

function removeItem(option: MultiselectOption) {
  if (!props.allowEmpty && selectedItems.value.length <= 1) return
  const updated = selectedItems.value.filter(item => item[props.trackBy] !== option[props.trackBy])
  emit('update:modelValue', updated)
  emit('remove', option)
}

function toggleSelectAll() {
  if (isAllSelected.value) {
    emit('update:modelValue', [])
  } else {
    const maxItems = props.max ? props.options.slice(0, props.max) : props.options
    emit('update:modelValue', [...maxItems])
  }
}

function addTag() {
  const value = newTagValue.value.trim()
  if (!value) return
  const newOption: MultiselectOption = { [props.label]: value, [props.trackBy]: value }
  addItem(newOption)
  newTagValue.value = ''
}

// --- Positioning ---
function positionDropdown() {
  if (!containerRef.value) return
  const rect = containerRef.value.getBoundingClientRect()
  const gap = 4
  const spaceBelow = window.innerHeight - rect.bottom
  const openUp = spaceBelow < 300 && rect.top > 300

  dropdownStyle.value = {
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    ...(openUp
      ? { bottom: `${window.innerHeight - rect.top + gap}px`, top: 'auto' }
      : { top: `${rect.bottom + gap}px`, bottom: 'auto' }
    ),
  }
}

function onScrollResize() {
  if (isOpen.value) positionDropdown()
}

watch(isOpen, (open) => {
  if (open) {
    window.addEventListener('scroll', onScrollResize, true)
    window.addEventListener('resize', onScrollResize)
  } else {
    window.removeEventListener('scroll', onScrollResize, true)
    window.removeEventListener('resize', onScrollResize)
  }
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScrollResize, true)
  window.removeEventListener('resize', onScrollResize)
  document.removeEventListener('mousedown', handleOutsideClick)
})
</script>

<style scoped>
.multiselect-v2.skeleton-play > * {
  opacity: 0;
}
</style>
